import { useEffect, useRef, useState } from 'react';
import { useEmitter } from '@/shared/lib/customHooks';
import { EMITTER_CONSTANTS, TAB_KEY } from '@/shared/config';
import emitter from '@/shared/lib/eventEmitter';
import { fetchAIProcessingStatus } from '@/entities/report';
import type { StickerCommandsProps } from './index';

/**
 * Drives the floating sticker listing the AI worker commands still running for a report. Positions the sticker left on the PDF report tab and right elsewhere, polls the worker once on first load and then every minute until saving is done, clears that interval on the AI loading and stop-saving emitter events, and tears the timers down on unmount.
 *
 * @param props - Sticker context carrying activeTab, profileId and studyFid.
 * @returns An object carrying showSticker, position and commands.
 */
const useStickerCommands = ({ activeTab, profileId, studyFid }: StickerCommandsProps) => {
  const [showSticker, setShowSticker] = useState(false);
  const [position, setPositon] = useState('--right');
  const [isSaveDone, setIsSaveDone] = useState(false);

  const [checkInterval, setCheckInterval] = useState<ReturnType<typeof setInterval> | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [commands, setCommands] = useState<string[]>([]);
  const intervalId = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    setPositon(activeTab === TAB_KEY.PDF_REPORT ? '--left' : '--right');
  }, [activeTab]);

  // Call the API immediately when the component first loads
  /**
   * Polls the AI worker for commands still pending on the open study and profile. Reads the last stored requestId out of localStorage, queries the processing commands, and either emits an AI loading signal while showing the sticker for ten seconds with the returned commands, or clears the loading signal and marks saving as done when the list is empty.
   *
   * @returns A promise resolving once the worker status has been applied to the sticker state.
   */
  const checkWorkerStatus = async () => {
    const keyStorage = `requestId-${profileId}-${studyFid}`;
    const storedResult = localStorage.getItem(keyStorage);
    const requestData = JSON.parse(storedResult as string);
    const { requestId } = requestData?.[requestData.length - 1] || {};
    // fetchAIProcessingStatus is declared to return string | boolean, but this
    // call site treats the 'processingCmd' result as a commands array.
    const result = (await fetchAIProcessingStatus(
      requestId,
      'processingCmd',
    )) as unknown as string[];
    if (result.length > 0) {
      emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: true, isSaving: false });
      setShowSticker(true);
      setIsSaveDone(false);
      setCommands(result);
      setTimeout(() => setShowSticker(false), 10000);
    } else {
      emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: false, isSaving: false });
      setShowSticker(false);
      setIsSaveDone(true);
    }
  };

  // Use useEffect to call the API immediately when the component is mounted for the first time
  useEffect(() => {
    if (isFirstLoad) {
      checkWorkerStatus();
      setIsFirstLoad(false);
    }
    if (!isSaveDone) {
      // Check again after 1 minute
      intervalId.current = setInterval(() => {
        checkWorkerStatus();
      }, 60000);
      setCheckInterval(intervalId.current);
    }
    return () => clearInterval(intervalId.current);
  }, [isSaveDone]);

  // check command when button loading
  useEmitter(
    EMITTER_CONSTANTS.AI_LOADING,
    (msg: { isLoading?: boolean; isSaving?: boolean }) => {
      const { isLoading, isSaving } = msg || {};
      if (isLoading && isSaving) {
        setIsSaveDone(false);
      } else {
        setIsSaveDone(true);
        setShowSticker(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (isSaveDone) {
      clearInterval(checkInterval);
      clearTimeout(checkInterval);
    }
  }, [isSaveDone, checkInterval]);

  useEmitter(
    EMITTER_CONSTANTS.STOP_SAVING,
    (msg: { data?: string }) => {
      if (msg) {
        if (msg?.data === 'done') {
          clearInterval(checkInterval);
        }
      }
    },
    [],
  );

  return { showSticker, position, commands };
};

export default useStickerCommands;
